// src/pages/firm/label/LabelTemplateList.tsx

import React, { useState } from "react";
import { useNavigate } from "react-router-dom"; // Editöre yönlendirmek için
import { EDataTable } from "../../../components/EDataTable";
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { TableActions } from "../../../components/TableActions";
import { Edit, Trash2, FileCode, Printer, CheckCircle, AlertTriangle, Copy } from "lucide-react"; 
import { LabelTemplateDialog } from "./label_template_dialog"; 
import { toast } from "react-toastify";

// Tipler ve Store (Senin yapıya göre uyarlandı)
import { LabelTemplate, LabelTemplatePayload } from "../../../types/label";  
import { useLabelStore } from "../../../store/labelStore";  
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { DataTableColumn } from "../../../types"; 

export const LabelTemplateList = () => {
    const store = useLabelStore();
    const { deleteItem, updateItem, createItem } = store; 
    const { handleApiError } = useApiErrorHandler();
    const navigate = useNavigate();
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [templateToEdit, setTemplateToEdit] = useState<LabelTemplate | null>(null);
    const [templateToCopy, setTemplateToCopy] = useState<LabelTemplate | null>(null);

    const handleNew = () => {
        setTemplateToEdit(null);
        setTemplateToCopy(null);
        setIsModalOpen(true);
    };

    const handleEditMeta = (tpl: LabelTemplate) => {
        setTemplateToEdit(tpl);
        setTemplateToCopy(null);
        setIsModalOpen(true);
    };

    const handleCopyClick = (tpl: LabelTemplate) => {
        setTemplateToCopy(tpl);
        setTemplateToEdit(null);
        setIsModalOpen(true);
    };

    // Esas tasarım editörüne yönlendiren buton fonksiyonu
    const handleOpenDesigner = (tpl: LabelTemplate) => {
        navigate(`/firm/labels/designer/${tpl.id}`);
    };

    const { confirm, modal: confirmModal } = useConfirmModal();

    const handleDeleteClick = async (tpl: LabelTemplate) => {
        const ok = await confirm(`${tpl.name} isimli etiket şablonunu ve bağlı tüm tasarımları silmek istiyor musunuz?`);
        if (ok) {
            try {
                await deleteItem(tpl.id);
                toast.success("Etiket şablonu silindi");
            } catch (error) {
                handleApiError(error); 
            }
        }
    };

    const handleSaveTemplate = async (data: LabelTemplatePayload) => { 
        try {
            if (templateToEdit) {
                await updateItem(templateToEdit.id, data);
                toast.success("Şablon bilgileri güncellendi");
            } else if (templateToCopy) {
                data.designJson = templateToCopy.designJson || undefined;
                const newTpl = await createItem(data as any);
                
                // Varyantları kopyala
                if (templateToCopy.variants && templateToCopy.variants.length > 0) {
                    for (const variant of templateToCopy.variants) {
                        try {
                            await store.saveVariant({
                                templateId: newTpl.id as any,
                                language: variant.language,
                                widthMm: variant.widthMm || undefined,
                                heightMm: variant.heightMm || undefined,
                                content: variant.content
                            });
                        } catch (varErr) {
                            console.error("Varyant kopyalama hatası:", varErr);
                            toast.error(`${variant.language} tasarımı kopyalanamadı.`);
                        }
                    }
                }
                toast.success("Şablon kopyalandı");
            } else {
                await createItem(data as any);
                toast.success("Yeni etiket şablonu eklendi");
            }
            setIsModalOpen(false);
            setTemplateToCopy(null);
            setTemplateToEdit(null);
        } catch (error) {
            handleApiError(error); 
        }
    };

    // Etiketin hangi yazıcı dillerinde varyantları olduğunu gösteren küçük çipler
    const languagesBody = (tpl: LabelTemplate) => (
        <div className="flex gap-1 flex-wrap">
            {tpl.variants && tpl.variants.length > 0 ? (
                tpl.variants.map((v) => (
                    <span key={v.id} className="bg-slate-100 border border-slate-200 text-slate-700 px-2 py-0.5 rounded text-xs font-medium flex items-center">
                        <Printer className="w-3 h-3 mr-1 text-slate-500" />
                        {v.language} {v.tenantId ? "(Özel)" : "(Global)"}
                    </span>
                ))
            ) : (
                <span className="text-amber-500 text-xs flex items-center italic">
                    <AlertTriangle className="w-3 h-3 mr-1" /> Tasarım Eklenmemiş
                </span>
            )}
        </div>
    );

    const actionBody = (tpl: LabelTemplate) => (
        <TableActions
            actions={[
                {
                    icon: FileCode,
                    colorClass: "text-indigo-600 hover:bg-indigo-50",
                    title: "Tasarım Editörü",
                    onClick: () => handleOpenDesigner(tpl)
                },
                {
                    icon: Edit,
                    colorClass: "text-blue-600 hover:bg-blue-50",
                    title: "Şablonu Düzenle",
                    onClick: () => handleEditMeta(tpl)
                },
                {
                    icon: Copy,
                    colorClass: "text-green-600 hover:bg-green-50",
                    title: "Kopyala",
                    onClick: () => handleCopyClick(tpl)
                },
                {
                    icon: Trash2,
                    colorClass: "text-red-500 hover:bg-red-50",
                    title: "Sil",
                    onClick: () => handleDeleteClick(tpl)
                }
            ]}
        />
    );

    const columns: DataTableColumn<LabelTemplate>[] = [
        { field: "code", header: "Şablon Kodu", width: "200px", sortable: true },
        { field: "name", header: "Şablon Adı", width: "250px", sortable: true },
 
        { field: "actions", header: "İşlemler", body: actionBody, width: "150px", align: "center" },
    ];

    return (
        <div className="w-full h-full p-2 flex flex-col">
            {confirmModal}
            <div className="bg-card rounded-xl border border-borderColor shadow-sm overflow-hidden flex-1 flex flex-col">
                <EDataTable<LabelTemplate> 
                    tableKey="label_template_list"
                    useStore={() => store} 
                    dataKey="id"
                    title="Etiket Şablon Yönetimi"
                    placeholder="Şablon Adı veya Kodu Ara"
                    searchFields={[
                        { field: "name", operator: "contains" },
                        { field: "code", operator: "contains" },
                    ]}
                    actions={
                        <TextButton onClick={handleNew} className="btn-new">
                             Yeni Etiket Şablonu Tanımla
                        </TextButton>
                    }
                    columns={columns}
                />
            </div>

            <LabelTemplateDialog
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setTemplateToEdit(null);
                    setTemplateToCopy(null);
                }}
                template={templateToEdit || templateToCopy}
                isCopy={!!templateToCopy}
                onSave={handleSaveTemplate}
            />
        </div>
    );
};